import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Sparkles, Unlink, Check } from 'lucide-react';
import ConsumablePicker, { money } from './ConsumablePicker';
import { firstWord } from './KitEntryPrice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib/catalog-admin`;
const norm = (s) => (s || '').toLowerCase().replace(/[—–-]/g, ' ').replace(/\s+/g, ' ');

const FILTERS = [
  { id: 'check', label: 'Auto-linked · check', test: (i) => i.price.has_price && i.price.confidence === 'check' },
  { id: 'none', label: 'Not in consumables module', test: (i) => !i.price.has_price },
  { id: 'all', label: 'All', test: () => true },
];

function Confidence({ p }) {
  if (!p.has_price) return <span className="text-[11px] font-bold uppercase text-slate-600 bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5">no match</span>;
  if (p.source === 'manual') return <span className="text-[10px] uppercase text-slate-500">typed</span>;
  if (p.confidence === 'check') return <span className="text-[11px] font-bold uppercase text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">auto · {Math.round((p.score || 0) * 100)}% — check</span>;
  if (p.confidence === 'strong') return <span className="text-[11px] font-bold uppercase text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5">auto · {Math.round((p.score || 0) * 100)}%</span>;
  return <span className="text-[11px] font-bold uppercase text-blue-800 bg-blue-100 border border-blue-300 rounded px-1.5 py-0.5">confirmed</span>;
}

function Row({ item, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async (body, msg) => {
    setBusy(true);
    try { const r = await axios.put(`${API}/items/${item.id}`, body); onChanged(r.data); setOpen(false); toast.success(msg || `${item.label}: ${r.data.price.has_price ? money(r.data.price.unit_cost) + '/' + r.data.price.unit : 'price cleared'}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setBusy(false);
  };
  const p = item.price;
  return (
    <li className={`py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 ${p.has_price ? '' : 'bg-slate-50'}`} data-testid={`tlp-row-${item.id}`}>
      <span className="flex-1 min-w-[200px] text-sm text-slate-900"><b>{item.label}</b> <span className="text-xs text-slate-500">· {item.category}</span></span>
      <span className="text-sm min-w-[220px] flex items-center gap-1.5 flex-wrap" data-testid={`tlp-price-${item.id}`}>
        {p.has_price && <><b className="text-slate-900">{money(p.unit_cost)}</b><span className="text-slate-500">/{p.unit}</span>{p.ref_name && <span className="text-xs text-blue-700">↔ {p.ref_name}</span>}</>}
        <Confidence p={p} />
      </span>
      {open
        ? <ConsumablePicker onPick={(c) => save(c.consumable_ref ? { consumable_ref: c.consumable_ref } : { unit_cost: c.unit_cost, consumable_ref: null })} onClose={() => setOpen(false)} initialQuery={firstWord(item.label)} busy={busy} testId={`tlp-picker-${item.id}`} />
        : <span className="flex items-center gap-1.5">
          {p.confidence === 'check' && <button type="button" disabled={busy} onClick={() => save({ confirm_match: true }, `${item.label} ↔ ${p.ref_name} confirmed`)} className="h-8 px-2.5 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1" data-testid={`tlp-confirm-${item.id}`}><Check className="w-3.5 h-3.5" /> Looks right</button>}
          <button type="button" onClick={() => setOpen(true)} className={`h-8 px-2.5 rounded-lg text-xs font-bold inline-flex items-center gap-1 ${p.has_price ? 'bg-slate-100 text-slate-700' : 'bg-blue-600 text-white'}`} data-testid={`tlp-link-${item.id}`}><Link2 className="w-3.5 h-3.5" /> {p.has_price ? 'Change' : 'Link / price…'}</button>
          {p.has_price && <button type="button" onClick={() => save({ consumable_ref: null, unit_cost: null })} disabled={busy} className="h-8 px-2 rounded-lg text-xs text-slate-500 hover:bg-slate-100 inline-flex items-center gap-1" title="Remove price link" data-testid={`tlp-unlink-${item.id}`}><Unlink className="w-3.5 h-3.5" /></button>}
        </span>}
    </li>
  );
}

export default function ToolListPrices() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('check');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/items`, { params: { kind: 'consumable' } }); setItems(r.data.items || []); }
    catch { toast.error('Could not load the tool list'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const autoLink = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auto-link`, { relink_auto: false });
      toast.success(`Auto-link: ${r.data.linked_strong} linked, ${r.data.linked_check} linked but worth a look, ${r.data.unmatched} not in the consumables module`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-link failed'); }
    setBusy(false);
  };

  const priced = items.filter((i) => i.price.has_price).length;
  const counts = Object.fromEntries(FILTERS.map((f) => [f.id, items.filter(f.test).length]));
  const ql = norm(q.trim());
  const shown = items.filter((i) => FILTERS.find((f) => f.id === filter).test(i) && (!ql || norm(`${i.label} ${i.category} ${i.price.ref_name || ''}`).includes(ql)));

  return (
    <div className="space-y-3" data-testid="tool-list-prices">
      <div className="rounded-xl bg-white border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <p className="text-sm text-slate-800 flex-1 min-w-[260px]">Kit costing charges the consumables-module price. The tool list is matched to it automatically by name — <b className="text-slate-900" data-testid="tlp-count">{priced}/{items.length} priced</b>. Items the module doesn't carry stay unpriced until someone links or types a cost.</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tool list…" className="h-9 px-3 rounded-lg bg-white text-slate-900 text-sm w-48" data-testid="tlp-search" />
        <button type="button" onClick={autoLink} disabled={busy} className="h-9 px-3 rounded-lg bg-violet-600 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60" title="Match every unpriced tool-list item to the consumables module now" data-testid="tlp-autolink"><Sparkles className="w-4 h-4" /> {busy ? 'Linking…' : 'Auto-link now'}</button>
      </div>
      <div className="flex gap-1 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.id} type="button" onClick={() => setFilter(f.id)} data-testid={`tlp-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === f.id ? 'bg-white text-slate-900' : 'bg-slate-900/5 text-slate-800 hover:bg-slate-900/10'}`}>{f.label} <span className="opacity-70">({counts[f.id]})</span></button>
        ))}
      </div>
      <div className="bg-white rounded-2xl shadow p-4">
        {loading ? <p className="text-sm text-slate-500">Loading…</p> : shown.length === 0 ? <p className="text-sm text-slate-600" data-testid="tlp-empty">{ql ? 'Nothing matches that search.' : filter === 'check' ? 'Nothing to check — every automatic link has been confirmed or is a strong match.' : filter === 'none' ? 'Everything on the tool list has a price.' : 'Nothing here.'}</p>
          : <ul className="divide-y divide-slate-100" data-testid="tlp-list">{shown.map((i) => <Row key={i.id} item={i} onChanged={(u) => setItems((arr) => arr.map((x) => x.id === u.id ? u : x))} />)}</ul>}
      </div>
    </div>
  );
}
